import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import { Link } from "react-router-dom";
import { Button } from "@mui/material";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import { formatDate } from "../../utils/portalHelpers";
import { StatusChip, EmptyState, LoadingPanel, ErrorPanel, statusTone } from "../../components/ui";
import { partnerQueryKeys } from "../../api/queryKeys";
import { cancelPartnerAppointment, fetchPartnerAppointments } from "../../api/endpoints/workspace";
import usePartnerQuery from "../../hooks/usePartnerQuery";
import { canCancel, dateParts, safeMeetLink, splitByWhen, timeRange } from "./appointmentHelpers";
import BookAppointmentDialog from "./BookAppointmentDialog";

const PAGE_SIZE = 50;

const VIEWS = [
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
];

/**
 * Served by `/partner/appointments`, which scopes to this partner's own clients. The partner can
 * book new meetings with their clients and an office member, and cancel the ones they booked;
 * every other change stays with the office.
 * Split into what is still to come and what already happened, because a partner opens this page
 * to answer "what is next for my client", not to read a log.
 */
const AppointmentsPage = () => {
  const [view, setView] = useState("upcoming");
  const [page, setPage] = useState(0);
  const [booking, setBooking] = useState(false);
  const [cancelling, setCancelling] = useState(null);
  const queryClient = useQueryClient();
  const cancel = useMutation(() => cancelPartnerAppointment(cancelling.id), {
    onSuccess: () => {
      toast.success("Appointment cancelled. Your clients and the office have been told.");
      queryClient.invalidateQueries([...partnerQueryKeys.all, "appointments"]);
      setCancelling(null);
    },
    onError: (error) =>
      toast.error(
        error?.response?.data?.errorMessage ||
          error?.response?.data?.message ||
          "Could not cancel the appointment."
      ),
  });
  const params = useMemo(() => ({ page, size: PAGE_SIZE }), [page]);

  const { data, isLoading, isError, refetch } = usePartnerQuery(
    partnerQueryKeys.appointments(params),
    () => fetchPartnerAppointments(params),
    { keepPreviousData: true }
  );

  const { upcoming, past } = useMemo(() => splitByWhen(data?.content || []), [data]);
  const rows = view === "upcoming" ? upcoming : past;
  const totalPages = data?.totalPages ?? 0;

  const bookButton = (
    <Button
      variant="contained"
      size="small"
      disableElevation
      startIcon={<EventAvailableOutlinedIcon />}
      onClick={() => setBooking(true)}
    >
      Book appointment
    </Button>
  );

  return (
    <div>
      <BookAppointmentDialog open={booking} onClose={() => setBooking(false)} />
      <ConfirmDialog
        open={Boolean(cancelling)}
        title="Cancel appointment?"
        message={
          cancelling
            ? `"${cancelling.title || "Appointment"}" on ${formatDate(
                cancelling.date
              )} will be cancelled. Your clients get a cancellation email and the office is notified.`
            : ""
        }
        confirmLabel="Cancel appointment"
        cancelLabel="Keep it"
        confirmColor="error"
        loading={cancel.isLoading}
        onConfirm={() => cancel.mutate()}
        onCancel={() => setCancelling(null)}
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          {VIEWS.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setView(option.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold border-0 cursor-pointer ${
                view === option.id ? "bg-teal-600 text-white" : "bg-transparent text-slate-600"
              }`}
            >
              {option.label}
              <span className="ml-1.5 opacity-70">
                {option.id === "upcoming" ? upcoming.length : past.length}
              </span>
            </button>
          ))}
        </div>
        {bookButton}
      </div>

      {isLoading ? (
        <LoadingPanel label="Loading appointments…" />
      ) : isError ? (
        <ErrorPanel
          description="The server did not answer. Nothing is lost, try again."
          action={
            <button
              type="button"
              onClick={() => refetch()}
              className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              Try again
            </button>
          }
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title={view === "upcoming" ? "Nothing booked yet" : "No past appointments"}
          description={
            view === "upcoming"
              ? "Book a meeting with the office, or wait for them to book one with your client."
              : "Meetings move here once their date has passed."
          }
          action={view === "upcoming" ? bookButton : null}
        />
      ) : (
        <>
          <ul className="list-none p-0 m-0 space-y-2">
            {rows.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                appointment={appointment}
                onCancel={() => setCancelling(appointment)}
              />
            ))}
          </ul>

          {totalPages > 1 ? (
            <div className="flex items-center justify-between gap-3 mt-4">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((current) => Math.max(current - 1, 0))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-slate-500">
                Page {page + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((current) => current + 1)}
                className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold disabled:opacity-40"
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

const AppointmentRow = ({ appointment, onCancel }) => {
  const { day, month } = dateParts(appointment);
  const time = timeRange(appointment.startTime, appointment.endTime);
  return (
    <li className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white px-3 py-3">
      <div className="shrink-0 w-14 rounded-xl bg-teal-50 border border-teal-100 px-2 py-2 text-center">
        <p className="text-lg font-bold text-teal-800 m-0 leading-none tabular-nums">{day}</p>
        <p className="text-[10px] font-semibold text-teal-700 m-0 mt-1 tracking-wide">{month}</p>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <h3 className="text-sm font-semibold text-slate-900 m-0">
            {appointment.title || "Appointment"}
          </h3>
          <div className="flex flex-wrap items-center gap-1.5">
            {appointment.bookedByYou ? <StatusChip tone="teal">Booked by you</StatusChip> : null}
            <StatusChip tone={statusTone(appointment.status)}>
              {appointment.status || "Unknown"}
            </StatusChip>
          </div>
        </div>

        <p className="text-xs text-slate-500 m-0 mt-1">
          {formatDate(appointment.date)}
          {time ? ` · ${time}` : ""}
          {appointment.location ? ` · ${appointment.location}` : ""}
          {appointment.withName ? ` · with ${appointment.withName}` : ""}
        </p>

        {appointment.description ? (
          <p className="text-xs text-slate-600 m-0 mt-1.5 line-clamp-2">
            {appointment.description}
          </p>
        ) : null}

        <div className="mt-2">
          {appointment.clientId ? (
            <Link
              to={`/partner/clients/${appointment.clientId}`}
              className="text-xs font-semibold text-teal-700 no-underline hover:underline"
            >
              {appointment.clientName || "View client"}
            </Link>
          ) : (
            <span className="text-xs text-slate-500">
              {appointment.clientName || "Your client"}
            </span>
          )}
          {appointment.ownClientCount > 1 ? (
            <span className="ml-1 text-xs text-slate-500">
              and {appointment.ownClientCount - 1} more of your clients
            </span>
          ) : null}
          {safeMeetLink(appointment.meetLink) ? (
            <a
              href={safeMeetLink(appointment.meetLink)}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-3 text-xs font-semibold text-teal-700 no-underline hover:underline"
            >
              Join meeting
            </a>
          ) : null}
          {canCancel(appointment) ? (
            <button
              type="button"
              onClick={onCancel}
              className="ml-3 bg-transparent border-0 p-0 text-xs font-semibold text-rose-600 cursor-pointer hover:underline"
            >
              Cancel
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
};

export default AppointmentsPage;
